import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {RouteComponentProps} from "react-router";
import {
    ICustomerPortalService,
    OnlineRentersClaimRequest,
    Party,
    Policy,
    ProductsEnum,
    Types,
    ValuableItemWithStartDate,
    FalconNavigatorHeader,
    SESSION_STORAGE_KEY_CLAIM_POLICY
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {deleteSessionStorage, getSessionStorage, setSessionStorage} from "../../../../../CommonFunctions";
import * as moment from "moment";
import {FutureCoverBanner} from "./FutureCoverBanner";
import {BackButton} from "../../../../../components/navigation/BackButton";
import {NotesBeforeClaimSubmission} from "../../../../../components/claims/NotesBeforeClaimSubmission";
import {AccountHolderDetailsPanel} from "../../../../../components/claims/AccountHolderDetailsPanel";
import {RadioInput} from "../../../../../components/inputs/RadioInput";

interface IProps extends RouteComponentProps<{}> {}

interface IMakeRentersClaimPageState {
    rentersClaim: IRentersClaim;
    onlineRentersClaimRequest: OnlineRentersClaimRequest;
    valuableItems: ValuableItemWithStartDate[];
    party?: Party;
    selectedPolicy?: Policy;
}

interface IRentersClaim {
    party?: Party;
    isMobileValid: boolean;
    isEmailValid: boolean;
    email: string;
    claimReference: string;
    selectedPolicy?: Policy;
}

@authorize
export class MakeRentersClaimPage extends React.Component<IProps, IMakeRentersClaimPageState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IProps) {
        super(props);

        const selectedPolicy = getSessionStorage(SESSION_STORAGE_KEY_CLAIM_POLICY);
        let rentersClaimSession: IRentersClaim | null = getSessionStorage("rentersClaim");

        if (selectedPolicy && selectedPolicy.policyNumber !== rentersClaimSession?.selectedPolicy?.policyNumber){
            this.handleSessionClear();
            rentersClaimSession = null;
        }

        const onlineRentersClaimRequestSession: OnlineRentersClaimRequest = getSessionStorage("onlineRentersClaimRequest");
        const valuableItemsSession: ValuableItemWithStartDate[] = getSessionStorage("valuableItems");

        this.state = {
            party: rentersClaimSession?.party,
            selectedPolicy,
            rentersClaim: rentersClaimSession !== null ? rentersClaimSession : {
                isMobileValid: true,
                isEmailValid: true,
                email: "",
                claimReference: "",
                selectedPolicy
            } as IRentersClaim,
            onlineRentersClaimRequest: onlineRentersClaimRequestSession !== null ?
                onlineRentersClaimRequestSession :
                new OnlineRentersClaimRequest({
                    claimPolicyNumber: selectedPolicy?.policyNumber,
                    contactMobile: selectedPolicy?.policyHolder.phoneMobile,
                    contactEmail: selectedPolicy?.policyHolder.email
                }),

            valuableItems: valuableItemsSession !== null ? valuableItemsSession : []
        };

        this.handleContactDetailsChange = this.handleContactDetailsChange.bind(this);

        this.pluginTemplates.setPageTitle("Your Renters claims");
    }

    public componentDidMount(): void {
        window.scrollTo(0, 0);

        if (!this.state.party){
            this.customerPortalService.getProfile().subscribe((party: Party) => {
                this.setState({party},() => this.updateLocalStorage());
            });
        }

        if (!this.state.selectedPolicy){
            this.customerPortalService.getPolicies(ProductsEnum.RENTERS).subscribe((policies: Policy[]) => {
                if (policies && policies.length > 0){
                    this.setState({selectedPolicy:policies[0]},() => this.updateLocalStorage());
                }
            });
        }
    }

    public render() {
        const {party, selectedPolicy} = this.state;

        if (party && selectedPolicy) {
            const policyStartDate: string = moment(selectedPolicy.startDate).format("YYYY-MM-DD");
            const today: string = moment().format("YYYY-MM-DD");
            const policyStartDateFormatted: string = moment(policyStartDate).format("DD/MM/YYYY");
            const showBanner: boolean = moment(today).isBefore(policyStartDate);

            return (
                <React.Fragment>
                    <FalconHeader/>

                    <div className="page-contents">
                        <FalconNavigatorHeader back={{ action: this.handleSessionClear }} title="Make a Renters claim"/>
                        {showBanner && <FutureCoverBanner policyStartDate={policyStartDateFormatted}/>}
                        <AccountHolderDetailsPanel
                            policy={selectedPolicy}
                            contactPhone={this.state.onlineRentersClaimRequest.contactMobile}
                            contactEmail={this.state.onlineRentersClaimRequest.contactEmail}
                            handleDetailsChange={this.handleContactDetailsChange}
                        />
                    </div>
                    <div className="page-contents">
                        {this.renderOtherPanel(showBanner)}
                        <BackButton isTop={false}
                                    onButtonPress={this.handleSessionClear}/>
                    </div>

                    <FalconFooter/>
                </React.Fragment>
            )
        } else {
            return (<Overlay show={true}/>)
        }
    }

    private updateLocalStorage = (): void => {
        const rentersClaim: IRentersClaim = {...this.state.rentersClaim};
        const onlineRentersClaimRequest: OnlineRentersClaimRequest = {...this.state.onlineRentersClaimRequest};
        const {party,selectedPolicy} = this.state;
        if (party){
            rentersClaim.party = party;
            rentersClaim.email = party.email;
            onlineRentersClaimRequest.contactEmail = party.email;
            onlineRentersClaimRequest.contactMobile = party.mobileNumber;
        }
        if (selectedPolicy){
            rentersClaim.selectedPolicy = selectedPolicy;
            onlineRentersClaimRequest.claimPolicyNumber = selectedPolicy.policyNumber;
        }
        setSessionStorage("onlineRentersClaimRequest", onlineRentersClaimRequest);
        setSessionStorage("rentersClaim", rentersClaim);
        this.setState({rentersClaim});
        this.setState({onlineRentersClaimRequest});
    };

    private renderOtherPanel = (showButton: boolean): JSX.Element => {
        return (
            <React.Fragment>
                <NotesBeforeClaimSubmission showNextYearPremium={false}
                    localNumber="0800 030 4898"
                    abroadNumber="0120 2856 143"
                />
                <hr className="dashed-10 mt-4 mb-4"/>
                <RadioInput
                    label={<label>Do you have any <b>special requirements</b> or <b>vulnerabilities</b> you’d like to make us aware of so that we can handle your claim in a way that best suits you?</label>}
                    id="vulnerabilitiesId"
                    onChange={this.setVulnerableOrNo}
                    value={this.state.onlineRentersClaimRequest.haveVulnerabilities}
                    disabled={showButton}
                    inlineDisplay={true}
                />
                <hr className="dashed-10 mt-1 mb-4"/>
                <RadioInput
                    label={<label>Is your claim <b>urgent</b>?</label>}
                    id="isClaimUrgentId"
                    onChange={this.setUrgentClaim}
                    value={this.state.onlineRentersClaimRequest.claimUrgent}
                    disabled={showButton}
                    inlineDisplay={true}
                />
                <hr className="dashed-10 mt-1 mb-5"/>
                <div className="px-4 px-md-0 ml-md-19">
                    <button type="button" className="btn btn-primary btn-next plugin"
                            id="submitMakeRentersClaim"
                            disabled={!this.isFormValid(showButton)}
                            onClick={this.handleRedirect}>Next</button>
                </div>
            </React.Fragment>
        )
    };

    private handleRedirect = (event: any): void => {
        event.target.disabled ? event.preventDefault() :
            this.props.history.push("/dashboard/rentersClaims/whenAndWhere");
    };

    private isFormValid = (coverNotStarted: boolean): boolean => {
        return this.state.onlineRentersClaimRequest &&
            this.state.onlineRentersClaimRequest.haveVulnerabilities !== undefined &&
            this.state.onlineRentersClaimRequest.claimUrgent !== undefined &&
            this.state.rentersClaim.isEmailValid &&
            this.state.rentersClaim.isMobileValid &&
            !coverNotStarted;
    };

    private setVulnerableOrNo = (event: any): void => {
        const state: boolean = event.target.value === "true";
        const onlineRentersClaimRequest: OnlineRentersClaimRequest = {...this.state.onlineRentersClaimRequest};
        onlineRentersClaimRequest.haveVulnerabilities = state;
        this.setState({onlineRentersClaimRequest});
        setSessionStorage("onlineRentersClaimRequest", onlineRentersClaimRequest);
    };

    private setUrgentClaim = (event: any): void => {
        const state: boolean = event.target.value === "true";
        const onlineRentersClaimRequest: OnlineRentersClaimRequest = {...this.state.onlineRentersClaimRequest};
        onlineRentersClaimRequest.claimUrgent = state;
        this.setState({onlineRentersClaimRequest});
        setSessionStorage("onlineRentersClaimRequest", onlineRentersClaimRequest);
    };

    private handleContactDetailsChange(phone: string, email: string, phoneValid: boolean, emailValid: boolean): void {
        const onlineRentersClaimRequest: OnlineRentersClaimRequest = {...this.state.onlineRentersClaimRequest};
        const rentersClaim: IRentersClaim = {...this.state.rentersClaim!};

        onlineRentersClaimRequest.contactMobile = phone;
        onlineRentersClaimRequest.contactEmail = email;
        rentersClaim.email = email;
        rentersClaim.isEmailValid = emailValid;
        rentersClaim.isMobileValid = phoneValid;

        this.setState({
            rentersClaim,
            onlineRentersClaimRequest
        })

        setSessionStorage("onlineRentersClaimRequest", onlineRentersClaimRequest);
        setSessionStorage("rentersClaim", rentersClaim);
    }

    private handleSessionClear = (): void => {
        deleteSessionStorage("onlineRentersClaimRequest");
        deleteSessionStorage("valuableItems");
        deleteSessionStorage("rentersClaim");
        deleteSessionStorage("whenAndWhere");
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/subscription/renters/rentersClaims/MakeRentersClaimPage.tsx